import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { nationalPhoneSchema, toE164Phone, toNationalPhone } from '@services/phone'
import {
  isClientSigned,
  signingEnvelopeStatus,
} from '@src/pages/Company/components/agreementStatus'
import { useRegisterCompanyInfo } from './Context'
import { RegisterCompanyService } from './service'
import StepCompanyInfo from './components/StepCompanyInfo'
import StepLegalBusinessInfo from './components/StepLegalBusinessInfo'
import StepRepresentativeInfo from './components/StepRepresentativeInfo'
import StepMembershipAgreement from './components/StepMembershipAgreement'
import StepMembershipApplicationSubmitSuccessful from './components/StepMembershipApplicationSubmitSuccessful'
import StepCheckEmail from './components/StepCheckEmail'

const RegisterCompanySchema = yup.object().shape({
  // ─── Step 1: Company Information ─────────────────────────────────────────
  company_name: yup.string().required('Company name is required'),
  business_category: yup.object().required('Business category is required'),
  industry: yup.object().required('Industry is required'),
  // Optional per Figma (no *), but if filled it must look like a URL / domain —
  // lenient so a bare "www.everest.co.id" (the placeholder) passes, "asdf 123" doesn't.
  website: yup
    .string()
    .nullable()
    .test('url', 'Invalid website URL', (v) => !v || /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i.test(v.trim())),
  company_size: yup.object().required('Number of employee is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  logo: yup.mixed().required('Logo is required'),
  province: yup.object().required('Province is required'),
  city: yup.object().required('City is required'),
  district: yup.object().required('District is required'),
  subdistrict: yup.object().required('Subdistrict is required'),
  postal_code: yup.object().required('Postal code is required'),
  company_address: yup.string().required('Company address is required'),
  // ─── Step 2: Legal & Business Information ────────────────────────────────
  npwp: yup.string().required('Company NPWP is required'),
  nib: yup.string().required('NIB is required'),
  date_of_establishment: yup
    .string()
    .required('Date of establishment is required')
    .test('not-future', 'Date of establishment cannot be in the future', (v) => !v || new Date(v) <= new Date()),
  npwp_document: yup.mixed().required('NPWP document is required'),
  nib_document: yup.mixed().required('NIB document is required'),
  akta_pendirian_document: yup.mixed().required('Akta Pendirian is required'),
  akta_perubahan_terbaru_document: yup.mixed().required('Akta Perubahan Terbaru is required'),
  // ─── Step 3: Authorized Representative ───────────────────────────────────
  rep_name: yup.string().required('Full name is required'),
  rep_position: yup.string().nullable(),
  rep_email: yup.string().email('Invalid email').required('Corporate email is required'),
  rep_phone: nationalPhoneSchema(yup),
  surat_kuasa_document: yup.mixed().required('Surat kuasa is required'),
})

// Fields validated when leaving each step (single schema, per-step trigger).
const STEP_FIELDS = {
  1: [
    'company_name', 'business_category', 'industry', 'website', 'company_size',
    'email', 'logo', 'province', 'city', 'district', 'subdistrict', 'postal_code',
    'company_address',
  ],
  2: [
    'npwp', 'nib', 'date_of_establishment',
    'npwp_document', 'nib_document', 'akta_pendirian_document', 'akta_perubahan_terbaru_document',
  ],
  3: ['rep_name', 'rep_position', 'rep_email', 'rep_phone', 'surat_kuasa_document'],
  4: [],
}

function RegisterCompanyInfoForm({ activeStep, setActiveStep }) {
  const nav = useNavigate()
  const [accepted, setAccepted] = useState({ terms: false, privacy: false })
  const [submitting, setSubmitting] = useState(false)
  // DocuSign email mode: after the agreement is emailed (or on resume when an
  // envelope is already awaiting signature), show the check-email screen instead
  // of the TnC so the user can't re-submit / re-send the agreement.
  const [signingSent, setSigningSent] = useState(false)
  const [signerEmail, setSignerEmail] = useState('')
  const {
    companyId,
    submitStep1,
    submitStep2,
    submitStep3,
    submitStep4,
    resumeEnrollment,
    clearEnrollment,
  } = useRegisterCompanyInfo()

  const {
    control,
    trigger,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(RegisterCompanySchema),
    defaultValues: {
      company_name: '',
      business_category: null,
      industry: null,
      website: '',
      company_size: null,
      email: '',
      logo: null,
      province: null,
      city: null,
      district: null,
      subdistrict: null,
      postal_code: null,
      company_address: '',
      npwp: '',
      nib: '',
      date_of_establishment: '',
      npwp_document: null,
      nib_document: null,
      akta_pendirian_document: null,
      akta_perubahan_terbaru_document: null,
      rep_name: '',
      rep_position: '',
      rep_email: '',
      rep_phone: '',
    },
  })

  // ─── Resume a draft on mount ────────────────────────────────────────────────
  // The created company's id is persisted (Context); enrollment_step from the
  // BE drives which wizard step to land on. Text fields are prefilled; files
  // already uploaded in previous sessions stay on the server (the BE merges
  // existing documents, so they don't need re-uploading).
  const resumed = useRef(false)
  useEffect(() => {
    if (resumed.current || !companyId) return
    resumed.current = true
    resumeEnrollment().then(async (company) => {
      if (!company) return
      const rep = company.authorized_representatives?.find((r) => r.is_primary)
        || company.authorized_representatives?.[0]
      const asOption = (value) => (value ? { label: value, value } : null)
      reset({
        ...getValues(),
        company_name: company.legal_name || company.name || '',
        business_category: asOption(company.business_category),
        industry: asOption(company.industry),
        company_size: asOption(company.number_of_employees),
        website: company.company_website || '',
        email: company.email || '',
        province: asOption(company.province),
        city: asOption(company.city),
        district: asOption(company.district),
        subdistrict: asOption(company.subdistrict),
        postal_code: asOption(company.postal_code),
        company_address: company.company_address || '',
        npwp: company.npwp || '',
        nib: company.nib || '',
        date_of_establishment: company.date_of_establishment || '',
        rep_name: rep?.name || '',
        rep_position: rep?.job_title || '',
        rep_email: rep?.email || '',
        rep_phone: rep?.phone ? toNationalPhone(rep.phone) : '',
      })
      const step = Math.min(Math.max(company.enrollment_step || 1, 1), 4)
      // Step 4 reached: if the agreement was already emailed (envelope sent /
      // awaiting signature), show the check-email screen instead of the TnC so
      // the user can't re-submit. Completed → enrollment done (waiting approval).
      // A declined / voided envelope (e.g. after an agreement reset) is neither:
      // fall through to the TnC so the client signs a new one.
      if (step === 4) {
        try {
          const st = await RegisterCompanyService.signingStatus(companyId)
          const s = signingEnvelopeStatus(st?.data)
          if (s === 'completed') {
            clearEnrollment()
            nav('/company')
            return
          }
          if (s && s !== 'declined' && s !== 'voided') {
            setSignerEmail(rep?.email || company.email || '')
            setSigningSent(true)
            return
          }
        } catch {
          // No envelope yet (404) → let the user sign (fall through to the TnC).
        }
      }
      setActiveStep(step)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  // ─── Payload builders (FormData field names = BE multipart contract) ───────
  const buildStep1FormData = (data) => {
    const formData = new FormData()
    formData.append('legal_name', data.company_name || '')
    if (data.business_category) formData.append('business_category', data.business_category.value)
    if (data.industry) formData.append('industry', data.industry.value)
    if (data.company_size) formData.append('number_of_employees', data.company_size.value)
    if (data.website) formData.append('company_website', data.website)
    if (data.email) formData.append('email', data.email)
    if (data.province) formData.append('province', data.province.value)
    if (data.city) formData.append('city', data.city.value)
    if (data.district) formData.append('district', data.district.value)
    if (data.subdistrict) formData.append('subdistrict', data.subdistrict.value)
    if (data.postal_code) formData.append('postal_code', String(data.postal_code.value))
    if (data.company_address) formData.append('company_address', data.company_address)
    if (data.logo instanceof File) formData.append('logo', data.logo)
    return formData
  }

  const buildStep2FormData = (data) => {
    const formData = new FormData()
    if (data.npwp) formData.append('npwp', data.npwp)
    if (data.nib) formData.append('nib', data.nib)
    if (data.date_of_establishment) formData.append('date_of_establishment', data.date_of_establishment)
    if (data.npwp_document instanceof File) formData.append('npwp_doc', data.npwp_document)
    if (data.nib_document instanceof File) formData.append('nib_doc', data.nib_document)
    if (data.akta_pendirian_document instanceof File) formData.append('akta_pendirian', data.akta_pendirian_document)
    if (data.akta_perubahan_terbaru_document instanceof File) {
      formData.append('akta_perubahan_terbaru', data.akta_perubahan_terbaru_document)
    }
    return formData
  }

  const buildStep3FormData = (data) => {
    const formData = new FormData()
    if (data.rep_name) formData.append('full_name', data.rep_name)
    if (data.rep_position) formData.append('job_title', data.rep_position)
    if (data.rep_email) formData.append('email', data.rep_email)
    if (data.rep_phone) formData.append('phone', toE164Phone(data.rep_phone))
    if (data.surat_kuasa_document instanceof File) {
      formData.append('authority_letter', data.surat_kuasa_document)
    }
    return formData
  }

  // Each step renders inside the same non-remounting #right scroller, so its
  // scrollTop carries over — a long step 1 leaves the next step scrolled halfway
  // down. Reset to top on every step change (Next and Back).
  useEffect(() => {
    document.getElementById('right')?.scrollTo({ top: 0 })
  }, [activeStep])

  // ─── Step submit (Next) ─────────────────────────────────────────────────────
  const onNext = async () => {
    const valid = await trigger(STEP_FIELDS[activeStep])
    if (!valid) return

    const data = getValues()
    setSubmitting(true)
    try {
      if (activeStep === 1) {
        await submitStep1(buildStep1FormData(data))
        setActiveStep(2)
      } else if (activeStep === 2) {
        await submitStep2(buildStep2FormData(data))
        setActiveStep(3)
      } else if (activeStep === 3) {
        await submitStep3(buildStep3FormData(data))
        setActiveStep(4)
      } else if (activeStep === 4) {
        // Embedded signing → redirect to the in-app signing URL. Email signing →
        // the agreement is emailed to the signer; show the check-email screen and
        // keep the enrollment id so we can detect completion on their return.
        const data = await submitStep4()
        if (data?.signing_url) {
          window.location.href = data.signing_url
          return
        }
        // "Enrollment already completed": the client already signed (e.g. in
        // another tab) and the agreement is with CLIK — nothing to sign here.
        if (data?.enrollment_status === 'completed' || isClientSigned(data?.agreement_status)) {
          clearEnrollment()
          nav('/company')
          return
        }
        setSignerEmail(data?.signer_email || '')
        setSigningSent(true)
      }
    } catch {
      // Error toast already shown by the Context handlers.
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Cancel & save draft ────────────────────────────────────────────────────
  const onSaveDraft = async () => {
    const data = getValues()
    setSubmitting(true)
    try {
      if (activeStep === 1) {
        // A draft needs at least the company name as its anchor.
        const valid = await trigger(['company_name'])
        if (!valid) return
        await submitStep1(buildStep1FormData(data), { draft: true })
      } else if (activeStep === 2) {
        await submitStep2(buildStep2FormData(data), { draft: true })
      } else if (activeStep >= 3) {
        await submitStep3(buildStep3FormData(data), { draft: true })
      }
      nav('/company')
    } catch {
      // Error toast already shown by the Context handlers.
    } finally {
      setSubmitting(false)
    }
  }

  const onBackToCompanyMenu = () => nav('/company')

  // Agreement emailed / awaiting signature → check-email screen (auto-advances to
  // waiting-approval once the signer completes, on tab refocus).
  if (signingSent) {
    return (
      <StepCheckEmail
        signerEmail={signerEmail}
        companyId={companyId}
        onDone={() => {
          clearEnrollment()
          nav('/company')
        }}
      />
    )
  }

  return (
    <form
      id="right"
      className="flex-1 overflow-y-auto h-full"
      onSubmit={(e) => {
        e.preventDefault()
        onNext()
      }}
    >
      {activeStep === 1 && (
        <StepCompanyInfo
          control={control}
          errors={errors}
          trigger={trigger}
          watch={watch}
          setValue={setValue}
          isSubmitting={submitting}
          onSaveDraft={onSaveDraft}
          onBackToCompanyMenu={onBackToCompanyMenu}
        />
      )}

      {activeStep === 2 && (
        <StepLegalBusinessInfo
          control={control}
          errors={errors}
          trigger={trigger}
          watch={watch}
          setValue={setValue}
          isSubmitting={submitting}
          onBack={() => setActiveStep(1)}
          onSaveDraft={onSaveDraft}
        />
      )}

      {activeStep === 3 && (
        <StepRepresentativeInfo
          control={control}
          errors={errors}
          trigger={trigger}
          watch={watch}
          setValue={setValue}
          isSubmitting={submitting}
          onBack={() => setActiveStep(2)}
          onSaveDraft={onSaveDraft}
        />
      )}

      {activeStep === 4 && (
        <StepMembershipAgreement
          control={control}
          errors={errors}
          trigger={trigger}
          watch={watch}
          isSubmitting={submitting}
          onBack={() => setActiveStep(3)}
          onSaveDraft={onSaveDraft}
          accepted={accepted}
          setAccepted={setAccepted}
        />
      )}

      {activeStep === 5 && (
        <StepMembershipApplicationSubmitSuccessful onBackToCompanyList={() => nav('/company')} />
      )}
    </form>
  )
}

export default RegisterCompanyInfoForm
